import { Observable, BehaviorSubject } from 'rxjs';
import { Config } from '../config';
import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import { HttpClient } from '@angular/common/http';
import { PendingRequestModel } from '../models/chat-request.model';
import { PaginatedResponse } from '../models/paginated-response.model';
import { ChatActionModel, ChatMessagesModel, ChatModel, ChatPayloadModel, ContactResponseModel, ConversationModel } from '../models/chat.model';

@Injectable({
  providedIn: 'root'
})
export class ChatService extends HttpService {
  private _unreadChatsCount$ = new BehaviorSubject<number>(0);

  constructor(
    http: HttpClient,
  ) {
    super(http);
  }

  get unreadChatsCount$(): Observable<number> {
    return this._unreadChatsCount$.asObservable();
  }

  /** Aantal gesprekken met ongelezen berichten ophalen en de badge-waarde bijwerken */
  refreshUnreadChatsCount(): void {
    this.http.get<{ unread_chats_count: number }>(this.buildUrl('chat/unread-count'))
      .subscribe({
        next: (res) => this._unreadChatsCount$.next(res.unread_chats_count),
        error: (err) => console.error('Failed to fetch unread chats count', err)
      });
  }

  /**
   * @param userId id of the loggedin user
   * @param params for status and direction etc
   */
  get(userId: number, params?: any): Observable<PaginatedResponse<ContactResponseModel>> {
    return this.http.get<PaginatedResponse<ContactResponseModel>>(`${Config.apiUrl}/chat/list`, {
      params: this.serialize(params)
    });
  }

  getChatList(params?: any): Observable<PaginatedResponse<ContactResponseModel>> {
    return this.http.get<PaginatedResponse<ContactResponseModel>>(`${Config.apiUrl}/chat/open_chat`, {
      params: this.serialize(params)
    });
  }

  getChatRequests(requestType: string, userId: number, params?: any) {
    return this.http.get<PaginatedResponse<PendingRequestModel>>(this.buildUrl(`chat/${requestType}/request/pending/list/${userId}`), {
      params: this.serialize(params),
    });
  }

  delete(chatId: string) {
    return this.http.delete(this.buildUrl(`chat/${chatId}/delete`));
  }

  reportChat(chatId: number) {
    return this.http.delete(this.buildUrl(`chat/${chatId}/report`));
  }

  followChatUser(chatId: number) {
    return this.http.delete(this.buildUrl(`chat/${chatId}/report`));
  }

  removeChat(chatId: number) {
    return this.http.delete(this.buildUrl(`chat/history/${chatId}/delete`));
  }

  unfriend(chatId: number) {
    return this.http.get(this.buildUrl(`chat/${chatId}/unfriend`));
  }

  getChatDetail(chatId: string) {
    return this.http.get(this.buildUrl(`chat/details/${chatId}`));
  }

  getConversation(chatId, params?: any) {
    return this.http.get<PaginatedResponse<ConversationModel>>(this.buildUrl(`chat/${chatId}/message`), {
      params: this.serialize(params)
    });
  }

  getActivity(params?: any) {
    return this.http.get<PaginatedResponse<ChatMessagesModel>>(this.buildUrl('accepted-activity-matches'), {
      params: this.serialize(params),
    });
  }

  sendMessage(request: ChatPayloadModel) {
    return this.http.post(this.buildUrl(`chat/${request.match_id}/message`), request);
  }

  updatePickupChoice(chatId: string, deliveryMethod: 'pickup' | 'delivery') {
    return this.http.put(this.buildUrl(`chat/${chatId}/pickup-choice`), {delivery_method: deliveryMethod});
  }

  blockUser(request: ChatActionModel) {
    return this.http.post(this.buildUrl('accept-or-block'), request);
  }

  messagesRead(messageId: string) {
    return this.http.get(this.buildUrl(`chat/read-message/${messageId}`));
  }

  changeStatus(chatId: number, body: { status: string }) {
    return this.http.post(this.buildUrl(`chat/request/status/${chatId}`), body);
  }

  cancelChat(chatId: string) {
    return this.http.put(this.buildUrl(`chat/${chatId}/cancel`), {});
  }

  /**
   * Remove user contact
   *
   * @param id number, to remove user contact permanently
   */
  contactRemoval(id: number) {
    return this.http.delete(this.buildUrl(`chat/${id}/delete`));
  }

  /**
   * Send chat request
   *
   * @param userId number, to send chat request
   */
  sendRequest(userId: number) {
    return this.http.post(this.buildUrl(`chat/user/request/${userId}`), { message: 'Hi' });
  }

  /**
   * send chat request from profile directly
   *
   * @param id number, id of user
   */
  sendChatRequest(request: { p_id: number }) {
    return this.http.post<ChatModel>(this.buildUrl(`chat/create`), request);
  }
  /**
   * Send an order (pickup or delivery) to the cloud.
   * 
   * @param payload Object containing chat_id, type, and optional details
   * @returns Observable of the server response
   */
  // sendOrder(payload: { chat_id: string; type: 'pickup' | 'delivery'; details?: any }): Observable<any> {
  //   return this.http.post(`${this.buildUrl('shipping/save-shipping-detail')}`, payload);
  // }

  /**
   * Notify that an order has been completed.
   *
   * @param payload Object containing email and chat_id
   * @returns Observable of the server response
   */
  notifyOrderCompleted(payload: { email: string; chat_id: string }): Observable<any> {
    return this.http.post(`${this.buildUrl(`chat/${payload.chat_id}/order/completed`)}`, payload);
  }

}
